// Global search bar for Navbar — type-ahead with grouped results
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Search, X, BookOpen, Video, Users, GraduationCap, Radio, FileText } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const GROUP_META = {
  courses: { label: "Courses", icon: BookOpen, link: (r) => `/courses/${r.id}`, color: "fuchsia" },
  classes: { label: "Live Classes", icon: Video, link: (r) => `/live/${r.id}`, color: "rose" },
  batches: { label: "Batches", icon: GraduationCap, link: () => `/dashboard/admin`, color: "amber" },
  users: { label: "Users", icon: Users, link: () => `/dashboard/admin`, color: "purple" },
  webinars: { label: "Webinars", icon: Radio, link: (r) => `/webinar/${r.slug}`, color: "emerald" },
  resources: { label: "Resources", icon: FileText, link: (r) => r.url, external: true, color: "blue" },
};

export default function GlobalSearchBar() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!q || q.length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data);
      } catch { setResults(null); }
      finally { setBusy(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        if (!q) setExpanded(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [q]);

  // Keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setExpanded(true);
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === "Escape") { setExpanded(false); setOpen(false); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const openSearch = () => {
    setExpanded(true);
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  if (!user) return null;

  // Icon-only collapsed state
  if (!expanded) {
    return (
      <button
        onClick={openSearch}
        className="w-9 h-9 rounded-full bg-white/5 border border-white/10 hover:border-[#FF0066]/40 hover:bg-[#FF0066]/10 flex items-center justify-center transition group"
        title="Search (⌘K)"
        data-testid="global-search-icon"
      >
        <Search className="w-4 h-4 text-white/70 group-hover:text-[#FF0066]" />
      </button>
    );
  }

  return (
    <div className="relative" ref={ref} data-testid="global-search">
      <div className="flex items-center gap-2 bg-white/5 border border-[#FF0066]/40 rounded-full px-3 py-1.5 w-[260px] sm:w-[320px] focus-within:border-[#FF0066]">
        <Search className="w-3.5 h-3.5 text-[#FF0066]" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search courses, classes, users…"
          className="bg-transparent flex-1 text-xs text-white placeholder:text-white/40 focus:outline-none"
          data-testid="global-search-input"
        />
        <button onClick={() => { setQ(""); setResults(null); setExpanded(false); }} className="text-white/40 hover:text-white" title="Close"><X className="w-3.5 h-3.5" /></button>
      </div>

      {open && q.length >= 2 && (
        <div className="absolute top-full mt-2 right-0 w-[400px] max-h-[70vh] overflow-y-auto bg-[#121212] border border-white/10 rounded-xl shadow-2xl shadow-[#FF0066]/10 z-50" data-testid="search-results-panel">
          {busy && <div className="p-4 text-center text-white/40 text-sm">Searching…</div>}
          {!busy && results && results.total === 0 && (
            <div className="p-6 text-center text-white/40 text-sm">No results for "<b>{q}</b>"</div>
          )}
          {!busy && results && Object.entries(results.results).map(([key, items]) => {
            if (!items || items.length === 0) return null;
            const M = GROUP_META[key];
            if (!M) return null;
            return (
              <div key={key} className="border-b border-white/5 last:border-b-0">
                <div className={`px-3 py-2 text-[10px] uppercase tracking-wider text-${M.color}-300 font-bold bg-white/[0.02] flex items-center gap-1.5`}><M.icon className="w-3 h-3" /> {M.label} ({items.length})</div>
                {items.map((r) => {
                  const props = M.external
                    ? { href: M.link(r), target: "_blank", rel: "noreferrer" }
                    : { to: M.link(r) };
                  const Tag = M.external ? "a" : Link;
                  return (
                    <Tag key={r.id} {...props} onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 transition" data-testid={`search-result-${r.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">{r.title || r.name}</div>
                        <div className="text-[10px] text-white/50 truncate">{r.email || r.course_title || r.topic || r.scheduled_at || ""}</div>
                      </div>
                    </Tag>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
